import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { map, mergeMap, Observable, of } from 'rxjs';

import { MessagingEnvironmentService } from '../../../../core/services/messaging-environment.service';
import { AuthenticationService } from '../../../user/services/authentication.service';
import {
  ChannelConsent,
  ChannelRoles,
  ChannelStatus,
  ChannelType,
  IBulkLoadChannelFilters,
  IBulkLoadResponse,
  IChannel,
  IChannelByParams,
  IChannelCreationDTO,
  IChannelOwner,
} from '../../interfaces/IChannel';
import { DealerSettingName } from '../../interfaces/IDealerSettings';
import { IDealer } from '../../interfaces/IDealer';
import { IMessageableEntity } from '../../interfaces/IMessageableEntity';
import { WorkspaceFeatures } from '../../interfaces/IWorkspace';
import { ChannelsService } from '../channels.service';
import { PermissionsService } from '../permissions.service';
import { MessagingApiStateService } from './messaging-api-state.service';

interface IBulkLoadChannelsFilters {
  module: string;
  systemTags: string[];
  searchTerm?: string;
  [key: string]: number | string | boolean | string[] | undefined;
}

@Injectable()
export class ChannelsApiService {
  constructor(
    private httpClient: HttpClient,
    private env: MessagingEnvironmentService,
    private state: MessagingApiStateService,
    private authenticationService: AuthenticationService,
    private channelsService: ChannelsService,
    private permissionsService: PermissionsService
  ) {}

  getChannels() {
    const body = this.getFilterchannels();

    const manualFilters = this.state.channelsFilter.filters.filter((e) => {
      return e.isManualFilter && e.value && e.field === 'unreadMessages';
    });
    if (this.state.channelsFilter.page === 1 && this.state.channels.length && manualFilters?.length === 1) {
      return of(this.state.channels);
    }

    return this.httpClient
      .post<IBulkLoadResponse[]>(
        `${this.env.env.apiUrl + this.env.env.messagingApiPath}/member/bulk-load?page=${
          this.state.channelsFilter.page
        }&size=${this.state.channelsFilter.size}&sort=lastMessage.createdAt:desc`,
        { ...body }
      )
      .pipe(
        mergeMap((channels) => {
          const messagingChannels = channels.map((channel) => {
            return this.channelsService.setChannelMessagingApi(channel);
          });
          return of(messagingChannels);
        })
      );
  }

  getChannel(channelUID: string, channelByParams: IChannelByParams, ignoreTags: boolean) {
    const body = this.getChannelFilters(channelUID, channelByParams, ignoreTags);
    return this.httpClient
      .post<IBulkLoadResponse[]>(
        `${
          this.env.env.apiUrl + this.env.env.messagingApiPath
        }/member/bulk-load?page=1&size=1&sort=lastMessage.createdAt:desc`,
        body
      )
      .pipe(
        map((channels) => {
          if (!channels.length) {
            throw { status: 404, message: 'No se encontraron canales.' };
          }
          const channel = channels.map((member) => {
            return this.channelsService.setChannelMessagingApi(member, channelByParams);
          })[0];
          return channel;
        })
      );
  }

  getFilterchannels(): IBulkLoadChannelsFilters {
    const body: IBulkLoadChannelsFilters = {
      module: this.state.selectedWorkSpace?.id ?? '',
      systemTags: [],
    };
    if (this.state.channelsFilter?.searchTerm) {
      body.searchTerm = this.state.channelsFilter.searchTerm;
    }
    const filters = this.state.channelsFilter.filters.filter((filter) => this.channelsService.checkFiltersChannels(filter));
    if (filters?.length) {
      const backendFilters = filters.filter((e) => {
        return !e.isManualFilter;
      });
      if (backendFilters?.length) {
        for (const check of backendFilters) {
          if (check.value && check.field) {
            if (!check.isTagFilter) {
              if (check.comparationType !== 'array') {
                body[check.field] = check.value;
                if (check.isReverseFilter) {
                  body[check.field] = !check.value;
                }
              } else {
                if (!body[check.field]) body[check.field] = [];
                (body[check.field] as string[]).push(check.value as string);
              }
            } else {
              body.systemTags.push(`${check.field}:${this.state[`${check.field as keyof MessagingApiStateService}`]}`);
            }
          }
        }
        if (this.channelsService.isSMSandWhatsappActive(filters)) {
          delete body['routing'];
        }
      }
    }
    if (
      !this.permissionsService.checkCanUseWhatsapp() &&
      this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.canUseWhatsapp])
    ) {
      body['routing'] = 'SMS';
    }
    return body;
  }

  getChannelFilters(channelUID: string, channelByParams: IChannelByParams, ignoreTags: boolean): IBulkLoadChannelFilters {
    let body: IBulkLoadChannelFilters = {
      module: this.state.selectedWorkSpace?.id ?? '',
    };
    if (channelByParams) {
      if (channelByParams.phoneNumber) {
        body.routing = channelByParams.routing;
        body.toPhoneNumber = channelByParams.phoneNumber;
        if (channelByParams.startDateTime || channelByParams.endDateTime) {
          body.messages = body.messages ?? {};
        }
        if (channelByParams.startDateTime) {
          (body.messages ??= {}).fromDate = channelByParams.startDateTime;
        }
        if (channelByParams.endDateTime) {
          (body.messages ??= {}).toDate = channelByParams.endDateTime;
        }
        if (channelByParams.tags && !ignoreTags) {
          if (!body.messages) body.messages = {};
          body.messages.tags = channelByParams.tags;
        }
      }
    }
    if (channelUID) {
      body = {
        module: this.state.selectedWorkSpace?.id ?? '',
        channelUID: channelUID,
      };
    }
    return body;
  }

  pinChannel(channel: IChannel, pinned: boolean): Observable<unknown> {
    return this.httpClient.patch<unknown>(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/member/pin`, {
      pinned: pinned,
      memberUID: channel.member.uid,
    });
  }

  changeBotMode(channel: IChannel, value: boolean): Observable<unknown> {
    return this.httpClient.patch<unknown>(
      `${this.env.env.apiUrl + this.env.env.messagingApiPath}/channel/${channel.uid}/hand-off-mode`,
      {
        handOffMode: value ? 'AI' : 'HUMAN',
      }
    );
  }

  changeChannelEnabledStatus(channel: IChannel, value: boolean): Observable<unknown> {
    return this.httpClient.patch<unknown>(
      `${this.env.env.apiUrl + this.env.env.messagingApiPath}/channel/${channel.uid}/enabled`,
      {
        value,
      }
    );
  }

  muteChannel(channel: IChannel, mute: boolean): Observable<unknown> {
    return this.httpClient.patch<unknown>(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/member/mute`, {
      muted: mute,
      memberUID: channel.member.uid,
    });
  }

  createChannel(newChannel: IChannelCreationDTO, members: string[]): Observable<IChannel> {
    const membersFiltered = new Set(members);
    const membersResult = [...membersFiltered].filter((e) => e);
    return this.authenticationService.getUserProfile().pipe(
      mergeMap((res) => {
        return this.httpClient
          .post<{ member: IBulkLoadResponse; channel: IChannel }>(
            `${this.env.env.apiUrl + this.env.env.messagingApiPath}/channel/create`,
            {
              accountUID: res.accountUID,
              subAccountUID: res.subAccountMessageableEntity.subAccountUID,
              members: membersResult,
              displayName: newChannel.displayName,
              channelType: newChannel.type,
              isPrivate: newChannel.isPrivate,
            }
          )
          .pipe(
            map((res) => {
              res.member.channel = res.channel;
              res.member.role = newChannel.role;
              res.member.totalCount = members.length;
              res.member.createdAt = res.channel.createdAt;
              return this.channelsService.setChannelMessagingApi(res.member);
            })
          );
      })
    );
  }

  createChannelByParams(data: IChannelByParams): Observable<IChannel | null> {
    let consentStatus = ChannelConsent.GRANTED;
    if (this.permissionsService.checkDealerSetting(DealerSettingName.serviceExplicitOptInEnabled)) {
      consentStatus = ChannelConsent.NOT_APPLICABLE;
    }

    const body = {
      to: {
        phoneNumber: data.phoneNumber,
        displayName: data.displayName,
        externalID: data?.externalId || this.state.user?.subAccountMessageableEntity?.businessReference?.entityUID,
      },
      displayName: data.displayName,
      routing: data?.routing,
      module: this.state.selectedWorkSpace?.id || (data?.workspace as string),
      optOut: false,
      consentStatus,
      memberStatus: ChannelStatus.RESPONDED,
      assignSystemTags: [] as string[],
    };

    if (data.ownerId) {
      body.assignSystemTags.push(`ownerId:${data.ownerId}`);
    }
    if (data.ownerDisplayName) {
      body.assignSystemTags.push(`ownerDisplayName:${data.ownerDisplayName}`);
    }
    if (data.orderId) {
      body.assignSystemTags.push(`orderId:${data.orderId}`);
    }
    return this.httpClient
      .post<IChannel>(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/channel/direct`, body)
      .pipe(
        map((channel) => {
          if (data.isExternalCreate) {
            return null;
          }
          return this.channelsService.setPhoneChannelResponse(channel, data);
        })
      );
  }

  optIntOutState(enabled: boolean) {
    return this.httpClient.patch(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/member/optinoptout/status`, {
      memberUID: this.state.selectedChannel?.member?.uid,
      enabled,
    });
  }

  patchChannelInfo(channel: IChannel, displayName: string) {
    return this.httpClient.patch<unknown>(
      `${this.env.env.apiUrl + this.env.env.messagingApiPath}/channel/${channel.uid}/display-name`,
      {
        displayName,
      }
    );
  }

  patchChannelPhone(channel: IChannel, newPhone: string, oldPhone: string) {
    return this.httpClient.patch<unknown>(
      `${this.env.env.apiUrl + this.env.env.messagingApiPath}/messageable-entity/customer-phone-number`,
      {
        accountUID: this.state.user?.accountUID ?? '',
        subAccountUID: this.state.user?.subAccountMessageableEntity?.subAccountUID ?? '',
        currentPhoneNumber: oldPhone,
        newPhoneNumber: newPhone,
      }
    );
  }

  setReadChannel(channel: IChannel, value: boolean): Observable<unknown> {
    if (!channel?.unreadMessages && !channel?.unread && !value) {
      return of(null);
    }
    return this.httpClient.patch(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/member/read`, {
      memberUID: channel?.member?.uid,
      read: !value,
    });
  }

  readAllMessages(): Observable<unknown> {
    return this.httpClient.post(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/member/bulkMarkMembersAsRead`, {
      module: this.state.selectedWorkSpace?.id,
      ownerId: this.state.user?.subjectMessageableEntity?.businessReference?.entityUID ?? '',
    });
  }

  deleteMemberChannel(members: string[]): Observable<unknown> {
    return this.httpClient.delete(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/channel/group/member`, {
      body: {
        accountUID: this.state.user?.accountUID ?? '',
        subAccountUID: this.state.user?.subAccountMessageableEntity?.subAccountUID ?? '',
        channelUID: this.state.selectedChannel?.uid ?? '',
        messageableEntityUIDs: members,
      },
    });
  }

  addMembersChannel(members: string[]): Observable<unknown> {
    return this.httpClient.post(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/channel/group/member`, {
      accountUID: this.state.user?.accountUID ?? '',
      subAccountUID: this.state.user?.subAccountMessageableEntity?.subAccountUID ?? '',
      channelUID: this.state.selectedChannel?.uid ?? '',
      messageableEntityUIDs: members,
    });
  }

  getMembersChannel(page: number, size: number): Observable<IMessageableEntity[]> {
    return this.httpClient
      .post<{ members: IMessageableEntity[] }>(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/channel/members`, {
        size,
        from: page * size,
        channelUID: this.state.selectedChannel?.uid ?? '',
        accountUID: this.state.user?.accountUID ?? '',
      })
      .pipe(map((e) => e.members));
  }

  changeMemberRole(memberUid: string, rol: ChannelRoles): Observable<unknown> {
    return this.httpClient.patch(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/channel/group/role`, {
      accountUID: this.state.user?.accountUID ?? '',
      subAccountUID: this.state.user?.subAccountMessageableEntity?.subAccountUID ?? '',
      channelUID: this.state.selectedChannel?.uid ?? '',
      role: rol,
      messageableEntityUID: memberUid,
    });
  }

  leaveChannel(): Observable<unknown> {
    return this.httpClient.post(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/channel/group/leave`, {
      channelUID: this.state.selectedChannel?.uid ?? '',
      accountUID: this.state.user?.accountUID ?? '',
      subAccountUID: this.state.user?.subAccountMessageableEntity?.subAccountUID ?? '',
    });
  }

  joinChannel(channelUID: string): Observable<unknown> {
    return this.httpClient.post(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/channel/group/member`, {
      accountUID: this.state.user?.accountUID ?? '',
      subAccountUID: this.state.user?.subAccountMessageableEntity?.subAccountUID ?? '',
      channelUID: channelUID,
      messageableEntityUIDs: [this.state.user?.subjectMessageableEntity?.uid ?? ''],
    });
  }

  getChannelMembersPhotos(channel: IChannel): Observable<IMessageableEntity[]> {
    if (
      channel?.members?.length ||
      !this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.canSeeChatMembersPhotos]) ||
      channel.type === ChannelType.DIRECT_MESSAGE
    ) {
      return of(channel.members ?? []);
    }
    return this.httpClient.get<IMessageableEntity[]>(
      `${this.env.env.apiUrl + this.env.env.messagingApiPath}/member/channel/${channel.uid}`
    );
  }

  changeChannelStatus(channel: IChannel, value: ChannelStatus): Observable<unknown> {
    return this.httpClient.patch<unknown>(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/member/status`, {
      accountUID: this.state.user?.accountUID ?? '',
      status: value.toUpperCase(),
      uid: channel.member.uid,
    });
  }

  changeChannelOwner(channel: IChannel, owner: IChannelOwner): Observable<unknown> {
    return this.httpClient.patch<unknown>(
      `${this.env.env.apiUrl + this.env.env.messagingApiPath}/channel/${channel.uid}/tags`,
      {
        assignSystemTags: [`ownerId:${owner.id}`, `ownerDisplayName:${owner.displayName}`],
        removeSystemTagsPrefixPatterns: ['ownerId:', 'ownerDisplayName:'],
      }
    );
  }

  changeChannelOrderID(channel: IChannel, orderId: string): Observable<unknown> {
    return this.httpClient.patch<unknown>(
      `${this.env.env.apiUrl + this.env.env.messagingApiPath}/channel/${channel.uid}/tags`,
      {
        assignSystemTags: [`orderId:${orderId}`],
        removeSystemTagsPrefixPatterns: ['orderId:'],
      }
    );
  }

  getUnreads(): Observable<{ count: number }> {
    if (!this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.showUnreadsCounter])) {
      return of({ count: 0 });
    }
    const body = this.getFilterchannels();
    let url = `${this.env.env.apiUrl + this.env.env.messagingApiPath}/member/unread?`;
    for (const key in body) {
      if (key !== 'systemTags') {
        url = `${url}${key}=${body[key]}&`;
      } else {
        url = `${url}tags=${body[key]}&`;
      }
    }
    url = url.slice(0, -1);
    return this.httpClient.get<{ count: number }>(url);
  }

  getDealers(): Observable<IDealer[]> {
    return of([]);
  }

  selectDealer(_dealer: IDealer) {
    return of([]);
  }
}
